import { useEffect, useMemo, useState } from "react";
import { Alert, Container, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import iconApps from "../../assets/images/icon/icon.png";
import { verifyEmailRegistrationToken } from "../../api/VerifyEmailApi";
import { API_BASE_URL } from "../../config/apiConfig";
import { Trans, useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const CONSOLE_COUNTDOWN_SECONDS = 5;
const MOBILE_COUNTDOWN_SECONDS = 3;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [countdown, setCountdown] = useState(null);

  const token = searchParams.get("token") || "";
  const sourceParam = (searchParams.get("source") || "console").toLowerCase();
  const source = sourceParam === "mobile" ? "mobile" : "console";

  const mobileLoginUrl = useMemo(
    () => process.env.REACT_APP_MOBILE_LOGIN_URL || "com.sewoork.app://login",
    [],
  );

  const fallbackMobileUrl = useMemo(
    () =>
      process.env.REACT_APP_MOBILE_FALLBACK_URL ||
      "https://play.google.com/store/apps/details?id=com.sewoork.app",
    [],
  );

  const mobileRedirectGatewayUrl = useMemo(() => {
    const apiRoot = API_BASE_URL.replace(/\/v1\/?$/, "");
    const params = new URLSearchParams({
      target: mobileLoginUrl,
      fallback: fallbackMobileUrl,
    });

    return `${apiRoot}/verify/mobile-login?${params.toString()}`;
  }, [mobileLoginUrl, fallbackMobileUrl]);

  useEffect(() => {
    let cancelled = false;

    const runVerification = async () => {
      if (!token) {
        setStatus("error");
        setMessage(t("verify_email.token_missing"));
        setIsLoading(false);
        return;
      }

      const response = await verifyEmailRegistrationToken(token);

      if (cancelled) {
        return;
      }

      if (response?.status === 200) {
        setStatus("success");
        setMessage(response.message || t("verify_email.success"));
        setCountdown(
          source === "mobile"
            ? MOBILE_COUNTDOWN_SECONDS
            : CONSOLE_COUNTDOWN_SECONDS,
        );
      } else {
        setStatus("error");
        setMessage(response?.message || t("verify_email.failed"));
      }

      setIsLoading(false);
    };

    runVerification();

    return () => {
      cancelled = true;
    };
  }, [token, source]);

  useEffect(() => {
    if (status !== "success" || countdown === null) {
      return;
    }

    if (countdown <= 0) {
      if (source === "mobile") {
        window.location.href = mobileRedirectGatewayUrl;

        return;
      }

      navigate("/login");
      return;
    }

    const timer = window.setTimeout(() => {
      setCountdown((prev) => (prev === null ? prev : prev - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [countdown, status, source, navigate, mobileRedirectGatewayUrl]);

  return (
    <>
      <Helmet>
        <title>{t("verify_email.meta_title")}</title>
      </Helmet>

      <LanguageSwitcher placement="floating" syncServer={false} />
      <div className="center-content">
        <Container className="box-shadow">
          <img
            src={iconApps}
            alt={t("logo_alt")}
            style={{
              marginTop: "20px",
              marginBottom: "20px",
              width: "100px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          />

          <h2 className="text-center">{t("verify_email.title")}</h2>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status" />
              <p className="mt-3 mb-0">{t("verify_email.loading")}</p>
            </div>
          ) : (
            <>
              <Alert variant={status === "success" ? "success" : "danger"}>
                {message}
              </Alert>

              {status === "success" && source === "console" && (
                <p className="text-center mb-3">
                  <Trans t={t} i18nKey="verify_email.redirect_console" values={{ count: countdown }} components={{ b: <b /> }} />
                </p>
              )}

              {status === "success" && source === "mobile" && (
                <>
                  <p className="text-center mb-3">
                    <Trans t={t} i18nKey="verify_email.redirect_mobile" values={{ count: countdown }} components={{ b: <b /> }} />
                  </p>
                  <div className="text-center">
                    <a
                      href={mobileRedirectGatewayUrl}
                      className="w-100 btn-modal-login border-0 d-inline-block py-2 text-white text-decoration-none"
                    >
                      {t("verify_email.open_mobile")}
                    </a>
                  </div>
                </>
              )}

              <p className="text-center mt-3 mb-0">
                {t("verify_email.browser_login")}{" "}
                <Link to="/login">{t("verify_email.go_to_login")}</Link>
              </p>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default VerifyEmailPage;
